import { useState, useEffect } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  ClipboardList,
  Users,
  Package,
  Truck,
  QrCode,
  Bell,
  Inbox,
  Menu,
  X,
  Smartphone,
  LogOut,
  User,
  Settings,
  Building2,
  Recycle,
  Crown,
  UserCog,
  Calendar,
  PhoneIncoming,
  ShoppingCart,
  AlertTriangle,
  BarChart3,
  ChevronDown,
  DollarSign,
  Shield,
  Receipt,
  ClipboardCheck,
  HelpCircle,
  Bot,
  FileText,
  History,
  Sun,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Clock,
} from 'lucide-react';
import { PackagePlus, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { notificacionesAPI, empresaAPI, getUploadUrl } from '@/lib/api';
import API from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import { useTheme } from '@/hooks/useTheme';
import { useMensajesPoller } from '@/hooks/useMensajesPoller';
import SubestadosStrip from '@/components/SubestadosStrip';
import AsistenteIA from '@/components/AsistenteIA';
import PresupuestoAceptadoPopup from '@/components/PresupuestoAceptadoPopup';
import { KeyboardShortcuts } from '@/components/KeyboardShortcuts';
import { FichajeWidget } from '@/components/FichajeWidget';

const SIDEBAR_COLLAPSED_KEY = 'nexora-sidebar-collapsed';

function SidebarGroup({ label, children, collapsed, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const hasActiveChild = Array.isArray(children)
    ? children.some((child) => child?.props?.className?.includes?.('active'))
    : false;

  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  if (collapsed) {
    // In collapsed mode, render the children directly without the group header
    return <div className="mb-2 space-y-0.5">{children}</div>;
  }

  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="sidebar-group-label w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted hover:text-sidebar-foreground transition-colors"
      >
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="space-y-0.5 mt-0.5">{children}</div>}
    </div>
  );
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
  });
  const [notificacionesPendientes, setNotificacionesPendientes] = useState(0);
  const [nuevasOrdenesCount, setNuevasOrdenesCount] = useState(0);
  const [insuramaInboxNoLeidas, setInsuramaInboxNoLeidas] = useState(0);
  const [alertasCaducadas, setAlertasCaducadas] = useState(0);
  const [correoNoLeidos, setCorreoNoLeidos] = useState(0);
  const [empresaLogo, setEmpresaLogo] = useState(null);
  const [empresaNombre, setEmpresaNombre] = useState('Mi Empresa');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, isTecnico, isMaster } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // WebSocket real-time notifications
  const wsToken = user?.token || localStorage.getItem('token');
  useWebSocket(wsToken);

  // Polling cada 30s: avisa con toast cuando llegan mensajes internos nuevos
  useMensajesPoller(user);

  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [sidebarCollapsed]);

  // Refresh notification count when WS message arrives
  useEffect(() => {
    const handleWsNotification = () => {
      notificacionesAPI.listar(true).then(res => {
        let data = res.data || [];
        if (isTecnico() && user) {
          const TIPOS_TECNICO = ['mensaje_admin', 'orden_desbloqueada', 'orden_asignada', 'material_aprobado'];
          data = data.filter(n => TIPOS_TECNICO.includes(n.tipo) && (!n.usuario_destino || n.usuario_destino === user?.id));
        }
        setNotificacionesPendientes(data.length);
      }).catch(() => {});
    };
    window.addEventListener('ws-notification', handleWsNotification);
    return () => window.removeEventListener('ws-notification', handleWsNotification);
  }, [isTecnico, user]);

  const navItemClass = ({ isActive }) => `nav-item ${isActive ? 'active' : ''}`;

  useEffect(() => {
    const fetchEmpresa = async () => {
      try {
        const res = await empresaAPI.obtener();
        if (res.data) {
          setEmpresaNombre(res.data.nombre || 'Mi Empresa');
          const raw = res.data.logo?.url || res.data.logo_url;
          if (raw) {
            setEmpresaLogo(raw.startsWith('http') ? raw : getUploadUrl(raw));
          }
        }
      } catch {
        // silently fail
      }
    };
    fetchEmpresa();
  }, []);

  useEffect(() => {
    const fetchNotificaciones = async () => {
      // Skip si la pestaña está en background
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await notificacionesAPI.listar(true);
        let data = res.data || [];
        if (isTecnico() && user) {
          const TIPOS_TECNICO = ['mensaje_admin', 'orden_desbloqueada', 'orden_asignada', 'material_aprobado'];
          data = data.filter(n => {
            const tipoPermitido = TIPOS_TECNICO.includes(n.tipo);
            const esParaMi = !n.usuario_destino || n.usuario_destino === user?.id;
            return tipoPermitido && esParaMi;
          });
        }
        setNotificacionesPendientes(data.length);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };

    if (isAdmin() || isTecnico()) {
      fetchNotificaciones();
      const interval = setInterval(fetchNotificaciones, 240000); // 4 min (antes 3)
      const handleUpdate = () => fetchNotificaciones();
      window.addEventListener('notificaciones-updated', handleUpdate);
      return () => {
        clearInterval(interval);
        window.removeEventListener('notificaciones-updated', handleUpdate);
      };
    }
  }, [isAdmin, isTecnico, user]);

  // Alertas (sub-estados caducados) — badge en sidebar
  useEffect(() => {
    if (!isAdmin()) return undefined;
    const fetchAlertas = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const { data } = await API.get('/alertas/contadores');
        setAlertasCaducadas(data?.caducados || 0);
      } catch {
        // silently fail
      }
    };
    fetchAlertas();
    const interval = setInterval(fetchAlertas, 180000); // 3 min (antes 2)
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Correo · contadores no leídos para badge
  useEffect(() => {
    const fetchCorreo = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const { data } = await API.get('/email/counters');
        setCorreoNoLeidos(data?.no_leidos || 0);
      } catch {
        // silently fail (no hay cuenta configurada todavía)
      }
    };
    fetchCorreo();
    const interval = setInterval(fetchCorreo, 120000);
    return () => clearInterval(interval);
  }, []);

  // Insurama Inbox
  useEffect(() => {
    if (!isAdmin()) return;
    let cancelled = false;
    const fetchInbox = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const { insuramaAPI } = await import('@/lib/api');
        const { data } = await insuramaAPI.inboxResumen();
        if (!cancelled) setInsuramaInboxNoLeidas(data?.no_leidas || 0);
      } catch {
        // silent
      }
    };
    fetchInbox();
    const interval = setInterval(fetchInbox, 60000);
    const handler = () => fetchInbox();
    window.addEventListener('insurama-inbox-updated', handler);
    window.addEventListener('notificaciones-updated', handler);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('insurama-inbox-updated', handler);
      window.removeEventListener('notificaciones-updated', handler);
    };
  }, [isAdmin]);

  // Nuevas órdenes count
  useEffect(() => {
    const fetchNuevasOrdenes = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await API.get('/nuevas-ordenes/count');
        setNuevasOrdenesCount(res.data?.count || 0);
      } catch { /* silent */ }
    };
    if (isAdmin()) {
      fetchNuevasOrdenes();
      const interval = setInterval(fetchNuevasOrdenes, 180000);
      window.addEventListener('ws-notification', fetchNuevasOrdenes);
      return () => {
        clearInterval(interval);
        window.removeEventListener('ws-notification', fetchNuevasOrdenes);
      };
    }
  }, [isAdmin]);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/crm/login');
  };

  const NavItem = ({ path, icon: Icon, label, badge, testId }) => {
    const content = (
      <NavLink
        to={`/crm${path}`}
        className={navItemClass}
        data-testid={testId || `nav-${path.slice(1).replace(/\//g, '-').replace(/\?.*$/, '')}`}
        title={sidebarCollapsed ? label : undefined}
      >
        <Icon className="w-[18px] h-[18px] shrink-0" />
        <span className="nav-label text-sm truncate">{label}</span>
        {badge ? (
          <Badge
            variant={typeof badge === 'string' ? 'secondary' : 'destructive'}
            className={`nav-badge ml-auto text-[10px] h-5 min-w-[1.25rem] flex items-center justify-center ${
              typeof badge === 'string' ? 'bg-primary/15 text-primary border-0' : ''
            }`}
          >
            {badge}
          </Badge>
        ) : null}
      </NavLink>
    );

    if (!sidebarCollapsed) return content;
    return (
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="flex items-center gap-2">
          <span>{label}</span>
          {badge ? <span className="text-xs text-muted-foreground">({badge})</span> : null}
        </TooltipContent>
      </Tooltip>
    );
  };

  const sidebarWidth = sidebarCollapsed ? 'w-[68px]' : 'w-60';
  const mainOffset = sidebarCollapsed ? 'lg:ml-[68px]' : 'lg:ml-60';

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background text-foreground">
        {/* Mobile header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-sidebar border-b border-sidebar-border z-50 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            {empresaLogo ? (
              <img src={empresaLogo} alt={empresaNombre} className="h-8 w-auto object-contain" />
            ) : (
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Smartphone className="w-4 h-4 text-primary-foreground" />
              </div>
            )}
            <span className="font-semibold">{empresaNombre}</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={toggleTheme}
              data-testid="theme-toggle-mobile"
              aria-label="Cambiar tema"
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`${sidebarCollapsed ? 'sidebar-collapsed' : ''} fixed left-0 top-0 h-full ${sidebarWidth} bg-sidebar border-r border-sidebar-border z-50 transition-[width,transform] duration-200 lg:translate-x-0 flex flex-col ${
            sidebarOpen ? 'translate-x-0 w-60' : '-translate-x-full'
          }`}
          data-testid="sidebar"
        >
          {/* Brand */}
          <div className="h-14 flex items-center gap-2.5 px-3 border-b border-sidebar-border shrink-0">
            <div className="w-9 h-9 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
              {empresaLogo ? (
                <img src={empresaLogo} alt={empresaNombre} className="h-6 w-6 object-contain" />
              ) : (
                <Smartphone className="w-4 h-4 text-primary" />
              )}
            </div>
            <div className="sidebar-brand-text min-w-0 flex-1">
              <h1 className="font-semibold text-sm tracking-tight text-sidebar-foreground truncate">{empresaNombre}</h1>
              <p className="text-[9px] text-sidebar-muted uppercase tracking-[0.15em]">CRM / ERP</p>
            </div>
            {/* Collapse toggle - desktop only */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="hidden lg:inline-flex items-center justify-center h-7 w-7 rounded-md text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-hover transition-colors"
              data-testid="sidebar-collapse-toggle"
              aria-label={sidebarCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
              title={sidebarCollapsed ? 'Expandir' : 'Contraer'}
            >
              {sidebarCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            </button>
          </div>

          {/* User info — click → Mi cuenta */}
          <div className="px-3 py-2.5 border-b border-sidebar-border shrink-0">
            <NavLink
              to="/crm/mi-cuenta"
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md p-1 -m-1 transition-colors ${
                  isActive ? 'bg-sidebar-hover' : 'hover:bg-sidebar-hover'
                }`
              }
              data-testid="sidebar-user-link"
              title="Mi cuenta"
            >
              <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center shrink-0">
                <User className="w-3.5 h-3.5 text-primary" />
              </div>
              <div className="sidebar-user-meta min-w-0 flex-1">
                <p className="font-medium text-xs truncate text-sidebar-foreground">{user?.nombre}</p>
                <Badge
                  variant={isMaster() ? 'destructive' : isAdmin() ? 'default' : 'secondary'}
                  className="text-[9px] h-4 px-1.5 mt-0.5"
                >
                  {isMaster() ? 'Master' : isAdmin() ? 'Admin' : 'Técnico'}
                </Badge>
              </div>
            </NavLink>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-1.5 py-2 overflow-y-auto overflow-x-hidden">
            <SidebarGroup label="Principal" collapsed={sidebarCollapsed} defaultOpen>
              <NavItem path="/dashboard" icon={LayoutDashboard} label="Dashboard" />
              {isAdmin() && <NavItem path="/nuevas-ordenes" icon={PackagePlus} label="Nuevas Órdenes" badge={nuevasOrdenesCount} />}
              <NavItem path="/ordenes" icon={ClipboardList} label="Órdenes de Trabajo" />
              {isAdmin() && <NavItem path="/logistica" icon={Truck} label="Logística" />}
              {isAdmin() && <NavItem path="/calendario" icon={Calendar} label="Calendario" />}
              <NavItem path="/scanner" icon={QrCode} label="Escáner QR" />
              {isAdmin() && <NavItem path="/incidencias" icon={AlertTriangle} label="Incidencias" />}
              {isAdmin() && <NavItem path="/alertas" icon={AlertTriangle} label="Alertas Sub-Estado" badge={alertasCaducadas} testId="nav-alertas" />}
              <NavItem path="/notificaciones" icon={Bell} label="Notificaciones" badge={notificacionesPendientes} />
              <NavItem path="/registro-horario" icon={Clock} label="Registro Horario" />
              <NavItem path="/mi-cuenta" icon={User} label="Mi cuenta" />
              {isMaster() && <NavItem path="/security" icon={Shield} label="Seguridad" />}
              {isAdmin() && (
                <NavItem
                  path="/notificaciones?cat=PROVEEDORES"
                  icon={Inbox}
                  label="Bandeja Insurama"
                  badge={insuramaInboxNoLeidas}
                  testId="nav-insurama-inbox"
                />
              )}
            </SidebarGroup>

            {isAdmin() && (
              <>
                <SidebarGroup label="Gestión" collapsed={sidebarCollapsed}>
                  <NavItem path="/clientes" icon={Users} label="Clientes" />
                  <NavItem path="/peticiones-exteriores" icon={PhoneIncoming} label="Peticiones Ext." />
                  <NavItem path="/inventario" icon={Package} label="Inventario" />
                  <NavItem path="/proveedores" icon={Truck} label="Proveedores" />
                  <NavItem path="/compras" icon={FileText} label="Compras" />
                  <NavItem path="/ordenes-compra" icon={ShoppingCart} label="Órdenes Compra" />
                </SidebarGroup>

                <SidebarGroup label="Operaciones" collapsed={sidebarCollapsed}>
                  <NavItem path="/restos" icon={Recycle} label="Restos" />
                </SidebarGroup>

                <SidebarGroup label="Comunicaciones" collapsed={sidebarCollapsed}>
                  <NavItem path="/email-config" icon={Settings} label="Notificaciones Auto" />
                </SidebarGroup>

                <SidebarGroup label="Finanzas y Logística" collapsed={sidebarCollapsed}>
                  <NavItem path="/finanzas" icon={BarChart3} label="Finanzas" />
                  <NavItem path="/gls-config" icon={Truck} label="GLS Config (legacy)" />
                </SidebarGroup>

                <SidebarGroup label="Integraciones" collapsed={sidebarCollapsed}>
                  <NavItem path="/ajustes/gls" icon={Truck} label="GLS · Ajustes" />
                  <NavItem path="/buscar-siniestro" icon={Shield} label="Buscar Siniestro" />
                  {isMaster() && <NavItem path="/insurama" icon={Settings} label="Config. Insurama" />}
                </SidebarGroup>
              </>
            )}

            {isAdmin() && (
              <SidebarGroup label="Herramientas Admin" collapsed={sidebarCollapsed}>
                <NavItem path="/agentes" icon={Bot} label="Agentes IA" badge="Nuevo" />
                <NavItem path="/agente-aria" icon={Bot} label="ARIA (IA)" />
                <NavItem path="/historial-impresion" icon={Printer} label="Impresión" />
                <NavItem path="/imprimir-pericial" icon={FileText} label="Informe Técnico" />
              </SidebarGroup>
            )}

            {isMaster() && (
              <SidebarGroup label="Administración" collapsed={sidebarCollapsed}>
                <NavItem path="/control-cambios" icon={History} label="Control Cambios" />
                <NavItem path="/analiticas" icon={BarChart3} label="Analíticas" />
                <NavItem path="/iso" icon={ClipboardCheck} label="ISO / WISE" />
                <NavItem path="/faqs-admin" icon={HelpCircle} label="FAQs Web" />
                <NavItem path="/usuarios" icon={UserCog} label="Usuarios" />
                <NavItem path="/empresa" icon={Building2} label="Empresa" />
                <NavItem path="/configuracion" icon={Settings} label="Configuración" />
                <NavItem path="/master" icon={Crown} label="Panel Master" />
              </SidebarGroup>
            )}
          </nav>

          {/* Footer: fichaje widget + theme toggle + logout + atajos */}
          <FichajeWidget collapsed={sidebarCollapsed} />
          <div className="border-t border-sidebar-border shrink-0 p-2 space-y-1">
            <Button
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-0' : 'justify-start'} gap-2 text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-hover h-8 text-xs`}
              onClick={toggleTheme}
              data-testid="theme-toggle"
              title={theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              <span className="sidebar-logout-text">{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
            </Button>
            <Button
              variant="ghost"
              className={`w-full ${sidebarCollapsed ? 'justify-center px-0' : 'justify-start'} gap-2 text-sidebar-muted hover:text-destructive hover:bg-destructive/10 h-8 text-xs`}
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Cerrar sesión"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="sidebar-logout-text">Cerrar Sesión</span>
            </Button>
            {!sidebarCollapsed && (
              <div className="pt-1 border-t border-sidebar-border/50 mt-1">
                <div className="px-2 pt-2 pb-0.5 flex items-center justify-between">
                  <span className="text-[9px] uppercase tracking-wider text-sidebar-muted font-semibold">Atajos</span>
                </div>
                <div className="px-2 space-y-1 text-[10px] text-sidebar-muted leading-tight">
                  <div className="flex items-center justify-between">
                    <span>Buscar cliente</span>
                    <kbd className="px-1.5 py-0.5 bg-sidebar-hover border border-sidebar-border rounded font-mono text-[9px]">Ctrl B</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Registrar recibido</span>
                    <kbd className="px-1.5 py-0.5 bg-sidebar-hover border border-sidebar-border rounded font-mono text-[9px]">Ctrl R</kbd>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Abrir orden</span>
                    <kbd className="px-1.5 py-0.5 bg-sidebar-hover border border-sidebar-border rounded font-mono text-[9px]">Ctrl A</kbd>
                  </div>
                </div>
              </div>
            )}
            {!sidebarCollapsed && (
              <p className="text-[10px] text-sidebar-muted text-center pt-1">v1.3.0</p>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className={`${mainOffset} min-h-screen pt-14 lg:pt-0 transition-[margin] duration-200`}>
          <div className="p-4 lg:p-8">
            {isAdmin() && <SubestadosStrip />}
            <Outlet />
          </div>
        </main>

        {/* Floating */}
        <AsistenteIA />
        <PresupuestoAceptadoPopup />
        <KeyboardShortcuts />
      </div>
    </TooltipProvider>
  );
}
